import React, { useCallback, useState } from 'react';
import { Flex, Box, GlobalIcons, IconArrow1, IconArrow2 } from '@creditas-ui/react';
import { MenuList, MenuItem, SubMenuList, SubMenuItem } from '@/components/Menu';
import { useGetAccountInformation } from '@/hooks/queries/useGetAccountInformation';
import { useHasPayrollProduct } from '@/hooks/queries/useHasPayrollProduct';
import { useWindowResize } from '@/hooks/helpers/useWindowResize';
import { redirectToPayrollDashboard, redirectToSupport } from '@/utils/redirect';
import {
  dispatchSimulateAtWorkLoanEvent,
  dispatchSimulateLoanEvent,
  dispatchSupportEvent,
} from '@/utils/analyticsEvents';
import { openSimulator } from '@/utils/simulator';
import { openFlows } from '@/utils/flows';
import i18n from '@/i18n';

type FnToggle = (bool: boolean) => boolean;
const toggle: FnToggle = (bool) => !bool;

type HeaderLinksProps = {
  children?: React.ReactNode;
};

const HeaderLinks = ({ children }: HeaderLinksProps): JSX.Element => {
  const { isSuccess } = useGetAccountInformation();
  const { isMobile } = useWindowResize();
  const payroll = useHasPayrollProduct();
  const [hasShowSubMenu, setHasShowSubMenu] = useState(false);

  const redirectToAtWorkCallback = (): void => {
    dispatchSimulateAtWorkLoanEvent();
    redirectToPayrollDashboard();
  };

  const handleAtWorkClick = (): void => {
    openFlows(redirectToAtWorkCallback);
  };

  const handleSimulateClick = (): void => {
    if (payroll.isSuccess && payroll.data) {
      handleAtWorkClick();
      return;
    }

    dispatchSimulateLoanEvent();
    openSimulator();
  };

  const showSubMenu = useCallback(() => {
    setHasShowSubMenu(true);
  }, []);

  const hideSubMenu = useCallback(() => {
    setHasShowSubMenu(false);
  }, []);

  const handleToggleSubMenu = useCallback(() => {
    setHasShowSubMenu(toggle);
  }, []);

  const handleSupport = useCallback(() => {
    dispatchSupportEvent();
    redirectToSupport();
  }, []);

  return (
    <MenuList>
      <MenuItem active onClick={(): void => {}}>
        {i18n.t('header:main')}
      </MenuItem>
      {payroll.isSuccess && (
        <MenuItem onClick={handleSimulateClick}>{i18n.t('header:simulate')}</MenuItem>
      )}
      {isSuccess && (
        <Box
          position="relative"
          onClick={handleToggleSubMenu}
          onMouseEnter={showSubMenu}
          onMouseLeave={hideSubMenu}
        >
          <MenuItem>
            <Flex alignItems="center">
              {i18n.t('header:benefits.label')}
              {hasShowSubMenu ? (
                <GlobalIcons Icon={IconArrow1} size={isMobile ? 'x_large' : 'medium'} />
              ) : (
                <GlobalIcons Icon={IconArrow2} size={isMobile ? 'x_large' : 'medium'} />
              )}
            </Flex>

            <SubMenuList open={hasShowSubMenu}>
              <SubMenuItem onClick={handleAtWorkClick}>
                {i18n.t('header:benefits.at_work')}
              </SubMenuItem>
            </SubMenuList>
          </MenuItem>
        </Box>
      )}
      <MenuItem onClick={handleSupport}>{i18n.t('header:support')}</MenuItem>
      {children}
    </MenuList>
  );
};

export { HeaderLinks };
